import { EventSourceController } from "event-source-plus";
import { ChatApi } from "../api/chatApi";

interface ProcessedMessage {
  messageId: string;
  timestamp: number;
}

export interface SseMessageHandler {
  onMessage: (msg: { data: string }) => void;
  onAbort?: (wasManualAbort: boolean) => void;
}

interface Connection {
  id: string;
  role: "primary" | "secondary";
  controller: EventSourceController;
}

export class SseConnectionManager {
  private static readonly REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes

  private projectId: string;
  private messageHandler: SseMessageHandler;
  private primaryConnection?: Connection;
  private secondaryConnection?: Connection;
  private lastRefreshTime: number = 0;
  private processedMessages: ProcessedMessage[] = [];
  private pendingConnectionSwitch = false;
  private connectionCounter = 0;
  private visibilityHandler?: (event: Event) => void;
  private refreshTimer?: NodeJS.Timeout;
  private isDisposed = false;
  private manualAbortIds = new Set<string>();

  constructor(projectId: string, messageHandler: SseMessageHandler) {
    this.projectId = projectId;
    this.messageHandler = messageHandler;
  }

  async start() {
    await this.establishPrimaryConnection();
    this.lastRefreshTime = Date.now();
    this.setupVisibilityHandler();
    this.scheduleRefresh();
  }

  private setupVisibilityHandler() {
    this.visibilityHandler = async () => {
      if (document.visibilityState === "visible" && !this.isDisposed) {
        const timeSinceLastRefresh = Date.now() - this.lastRefreshTime;
        if (timeSinceLastRefresh >= SseConnectionManager.REFRESH_INTERVAL) {
          await this.refreshConnections();
        } else if (
          !this.primaryConnection ||
          this.primaryConnection.controller.signal.aborted
        ) {
          await this.refreshConnections();
        } else {
          this.scheduleRefresh();
        }
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  private generateConnectionId(): string {
    return `conn_${++this.connectionCounter}`;
  }

  private async establishPrimaryConnection() {
    if (this.isDisposed) return;

    const connId = this.generateConnectionId();

    if (this.primaryConnection) {
      this.manualAbortIds.add(this.primaryConnection.id);
      this.primaryConnection.controller.abort();
    }

    const eventSource = await ChatApi.getSSE(this.projectId);
    let errorCount = 0;
    const controller = eventSource.listen({
      onMessage: (msg) => this.handleMessage(msg, connId),
      onRequestError: (_) => {
        errorCount++;
        if (errorCount >= 4) {
          controller.abort();
          this.primaryConnection = undefined;
        }
      },
      onResponseError: (_) => {
        errorCount++;
        if (errorCount >= 4) {
          controller.abort();
          this.primaryConnection = undefined;
        }
      },
    });

    // Add abort signal handler
    controller.signal.onabort = () => {
      const wasManualAbort = this.manualAbortIds.has(connId);
      if (wasManualAbort) {
        this.manualAbortIds.delete(connId);
      }

      if (this.primaryConnection?.id === connId) {
        this.primaryConnection = undefined;
      } else if (this.secondaryConnection?.id === connId) {
        this.secondaryConnection = undefined;
      }
      this.messageHandler.onAbort?.(wasManualAbort);
    };

    this.primaryConnection = {
      id: connId,
      role: "primary",
      controller,
    };
  }

  private async establishSecondaryConnection() {
    if (this.isDisposed) return;

    const connId = this.generateConnectionId();

    if (this.secondaryConnection) {
      this.manualAbortIds.add(this.secondaryConnection.id);
      this.secondaryConnection.controller.abort();
    }

    const eventSource = await ChatApi.getSSE(this.projectId);
    let errorCount = 0;
    const controller = eventSource.listen({
      onMessage: (msg) => this.handleMessage(msg, connId),
      onRequestError: (error) => {
        errorCount++;
        if (errorCount >= 4) {
          controller.abort();
          this.secondaryConnection = undefined;
        }
      },
      onResponseError: (error) => {
        errorCount++;
        if (errorCount >= 4) {
          controller.abort();
          this.secondaryConnection = undefined;
        }
      },
    });

    // Add abort signal handler
    controller.signal.onabort = () => {
      const wasManualAbort = this.manualAbortIds.has(connId);
      if (wasManualAbort) {
        this.manualAbortIds.delete(connId);
      }

      if (this.primaryConnection?.id === connId) {
        this.primaryConnection = undefined;
      } else if (this.secondaryConnection?.id === connId) {
        this.secondaryConnection = undefined;
      }
      this.messageHandler.onAbort?.(wasManualAbort);
    };

    this.secondaryConnection = {
      id: connId,
      role: "secondary",
      controller,
    };
  }

  private handleMessage(msg: { data: string }, connectionId: string) {
    if (this.isDisposed) {
      return;
    }

    const connection =
      this.primaryConnection?.id === connectionId
        ? this.primaryConnection
        : this.secondaryConnection;
    if (!connection) {
      return;
    }

    const data = JSON.parse(msg.data);
    const messageId = data.body.messageId;
    const timestamp = data.body.timestamp;

    // If we've already processed this message, skip it
    if (
      messageId &&
      timestamp &&
      this.isMessageProcessed(messageId, timestamp)
    ) {
      return;
    }

    // If this is the first message on the secondary connection during a refresh,
    // switch the connections immediately
    if (connection.role === "secondary" && this.pendingConnectionSwitch) {
      this.switchConnections();
    }

    // Add message to processed list and clean up old messages
    if (messageId && timestamp) {
      this.processedMessages.push({ messageId, timestamp });
      // Keep only last 1000 messages to prevent memory leaks
      if (this.processedMessages.length > 1000) {
        this.processedMessages = this.processedMessages.slice(-1000);
      }
    }

    this.messageHandler.onMessage(msg);
  }

  private isMessageProcessed(messageId: string, timestamp: number): boolean {
    return this.processedMessages.some(
      (msg) => msg.messageId === messageId && msg.timestamp === timestamp
    );
  }

  private async refreshConnections() {
    if (this.isDisposed) return;

    this.lastRefreshTime = Date.now();

    if (
      !this.primaryConnection ||
      this.primaryConnection.controller.signal.aborted
    ) {
      await this.establishPrimaryConnection();
    } else {
      this.pendingConnectionSwitch = true;
      await this.establishSecondaryConnection();
    }

    this.scheduleRefresh();
  }

  private scheduleRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = setTimeout(() => {
      if (document.visibilityState === "visible" && !this.isDisposed) {
        this.refreshConnections();
      }
    }, SseConnectionManager.REFRESH_INTERVAL);
  }

  private switchConnections() {
    if (
      !this.secondaryConnection ||
      this.secondaryConnection.controller.signal.aborted
    ) {
      // Clean up aborted secondary connection if it exists
      if (this.secondaryConnection?.controller.signal.aborted) {
        this.secondaryConnection = undefined;
      }
      return;
    }

    const oldPrimaryConnection = this.primaryConnection;

    // Update roles
    this.secondaryConnection.role = "primary";
    this.primaryConnection = this.secondaryConnection;
    this.secondaryConnection = undefined;

    // Close old primary connection
    if (oldPrimaryConnection) {
      this.manualAbortIds.add(oldPrimaryConnection.id);
      oldPrimaryConnection.controller.abort();
    }

    this.pendingConnectionSwitch = false;
  }

  dispose() {
    if (this.isDisposed) return;

    this.isDisposed = true;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }

    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = undefined;
    }

    if (this.primaryConnection) {
      this.primaryConnection.controller.abort();
      this.primaryConnection = undefined;
    }

    if (this.secondaryConnection) {
      this.secondaryConnection.controller.abort();
      this.secondaryConnection = undefined;
    }

    this.processedMessages = [];
    this.pendingConnectionSwitch = false;
    this.manualAbortIds.clear();
  }
}
